import _ from 'lodash'
import { DomainTuple } from 'victory'
import { Emission } from '../graphql/generated'
import { theme } from '../theme'

export const lineChartLineWidth = 3
export const lineChartDotSize = 5

/**
 * Represents a data point for display in a graph.
 */
export interface DataPoint {
  x: string
  y: number
}

/**
 * Represents a data point to display in graphs, which may be a "gap point",
 * i.e. it may represent a point with no Y value, in which case Y is null.
 * Note that Victory charts do not display data points where y is null,
 * but does account for them on the X-axis.
 */
export type FilledDataPoint = Omit<DataPoint, 'y'> & {
  y: DataPoint['y'] | null
}

export const getTotalCO2e = (emissions: Emission[]): number =>
  emissions.reduce((total, emission) => emission.totalCO2e + total, 0)

export const getMaxValue = (data: Array<{ y: number }>): number =>
  data.reduce(
    (prev, curr) => (curr.y > prev ? curr.y : prev),
    Number.MIN_SAFE_INTEGER,
  )

export const getMinValue = (data: Array<{ y: number }>): number =>
  data.reduce(
    (prev, curr) => (curr.y < prev ? curr.y : prev),
    Number.MAX_SAFE_INTEGER,
  )

export const getAverage = (data: Array<{ y: number }>): number => {
  const totalSum = data.reduce((sum, curr) => curr.y + sum, 0)
  return totalSum / data.length
}

export const getAverageOffset = (data: Array<{ y: number }>): number => {
  return getAverage(data) * 0.2
}

interface DomainOptions {
  offsetY?: number
  yMin?: number
}

export const calculateDomain = (
  data?: {
    x: number | string
    y: number
  }[],
  options: DomainOptions = {},
): { x: DomainTuple; y: DomainTuple } | undefined => {
  if (!data || data.length === 0) {
    return undefined
  }

  const yMax = getMaxValue(data)
  const yMin = options.yMin !== undefined ? options.yMin : getMinValue(data)
  const offset =
    options.offsetY !== undefined ? options.offsetY : getAverageOffset(data)

  const domain = {
    // when we have things like goals we may have several points for the same value of x
    // filter to get only unique values of x
    x: [0, new Set(data.map((d) => d.x)).size + 1] as DomainTuple,
    y: [Math.max(yMin - offset, 0), yMax + offset] as DomainTuple,
  }

  if (domain.y[0] === 0 && domain.y[1] === 0) {
    domain.y = [0, 10000]
  }

  return domain
}

const defaultMetricFont = `10px ${theme.typography.fontFamily}`

export function getTextMetrics(
  text: string,
  contextFont: string = defaultMetricFont,
):
  | {
      raw: TextMetrics
      font: { width: number; height: number }
      actual: { width: number; height: number }
    }
  | undefined {
  if (!text) {
    return undefined
  }
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) {
    return undefined
  }
  context.font = contextFont
  const rawMetrics = context.measureText(text)
  // Note: TextMetrics is not (guaranteed to be) an object, and cannot be spread,
  // so for convenience an equivalent object is constructed (note: TextMetrics may have more fields than these).
  type Writeable<T> = { -readonly [P in keyof T]: T[P] }
  const metrics: Writeable<
    Pick<
      TextMetrics,
      | 'actualBoundingBoxAscent'
      | 'actualBoundingBoxDescent'
      | 'actualBoundingBoxLeft'
      | 'actualBoundingBoxRight'
      | 'fontBoundingBoxAscent'
      | 'fontBoundingBoxDescent'
      | 'width'
    >
  > = {
    actualBoundingBoxAscent: rawMetrics.actualBoundingBoxAscent,
    actualBoundingBoxDescent: rawMetrics.actualBoundingBoxDescent,
    actualBoundingBoxLeft: rawMetrics.actualBoundingBoxLeft,
    actualBoundingBoxRight: rawMetrics.actualBoundingBoxRight,
    fontBoundingBoxAscent: rawMetrics.fontBoundingBoxAscent,
    fontBoundingBoxDescent: rawMetrics.fontBoundingBoxDescent,
    width: rawMetrics.width,
  }
  // Note: 'fontBoundingBoxAscent' et al require relatively newer browsers, so fallback to better-supported
  //        'actualBoundingBoxAscent' et al. (e.g. Firefox started supporting font* in summer of 2023).
  if (
    !_.isNumber(metrics.fontBoundingBoxAscent) ||
    !_.isNumber(metrics.fontBoundingBoxDescent)
  ) {
    metrics.fontBoundingBoxAscent = metrics.actualBoundingBoxAscent
    metrics.fontBoundingBoxDescent = metrics.actualBoundingBoxDescent
  }
  return {
    raw: Object.freeze(rawMetrics), // Freezing to comply with TextMetrics readonly state.
    font: {
      width: metrics.width,
      height: metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent,
    },
    actual: {
      width: metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight,
      height:
        metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent,
    },
  }
}

export function getTextWidth(
  text?: string,
  contextFont: string = defaultMetricFont,
): number {
  return getTextMetrics(text ?? '', contextFont)?.raw.width ?? 0
}

export function getActualTextHeight(
  text?: string,
  contextFont: string = defaultMetricFont,
): number {
  return getTextMetrics(text ?? '', contextFont)?.actual.height ?? 0
}

export function getTextFontHeight(
  text?: string,
  contextFont: string = defaultMetricFont,
): number {
  return getTextMetrics(text ?? '', contextFont)?.font.height ?? 0
}

/**
 * Fill in gaps in a given series of data points over years,
 * so that all years between the min and max are covered.
 *
 * Note: The years are expected to be strings that hold integers.
 *
 * For gap points, the Y value is set to null. Note: when Victory charts
 * are given a y value of null, they display no data point for that x value.
 *
 * @param data Data points where x is a year stored as a string
 * @returns A sorted list of the same points as the input, plus points
 *          filling any gap in span of years, setting y as null for those
 */
export function fillYearGaps(data: DataPoint[]): FilledDataPoint[] {
  if (!data.length) {
    return data
  }

  // Find all points in the input data, and store associated
  // by their year in a map for lookup.
  // By contract, the X values must be parsable as integers.
  const yearPointTuples: [number, DataPoint][] = data.map((p) => [
    Number.parseInt(p.x, 10),
    p,
  ])
  const yearLookup: Map<number, DataPoint> = new Map(yearPointTuples)
  // Get the range of X-values to fill, as numbers
  // (the list is not empty, so min and max always exist)
  const min = _.minBy(yearPointTuples, ([x]) => x)![0]
  const max = _.maxBy(yearPointTuples, ([x]) => x)![0]
  // Fill all x:s from min to max (inclusive), adding missing data points with y = null.
  // (it's pointless to check min/max as they exist by definition, but simplifies code a bit)
  const filledData = _.range(min, max + 1).map<FilledDataPoint>((x) => {
    const covered = yearLookup.get(x)
    if (covered) {
      return covered
    } else {
      return {
        x: x.toFixed(0),
        y: null,
      }
    }
  })

  return filledData
}

/**
 * Generate a trend line for a given set of sorted X values.
 *
 * @returns A sorted list of data points giving a trend line
 */
export function makeTrendLine({
  sortedXs,
  trendOffset,
  trendRate,
}: {
  /** Sorted X values to genereate a trend line over */
  sortedXs: string[]
  /** The starting Y-value of the trend line */
  trendOffset: number
  /** The linear rate of change in the trend per step on the X-axis */
  trendRate: number
}): DataPoint[] {
  return (
    sortedXs.map((x, i) => ({
      x: x,
      // Generate a trend line starting at the trend offset.
      // Max(0, ...) because we want to prevent any possibility of the trend going to negative emissions
      // (which should never happen unless we mess up the trend calculations)
      y: Math.max(0, trendOffset + trendRate * i),
    })) ?? []
  )
}
